import React, { useState, useEffect, useCallback } from "react";
import { formatTimestampDetailed } from "../utils/timestampUtils";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Send,
  CheckCircle,
  XCircle,
  Eye,
  Clock,
  Users,
  Download,
  Target,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Phone,
  BarChart3,
  MessageSquare,
  Smartphone,
  Calendar,
  MousePointerClick,
} from "lucide-react";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  getCampaignAnalyticsSummary,
  getCampaignDateWiseAnalytics,
  getCampaignContactsByStatus,
  exportCampaignContactsByStatus,
  recomputeCampaignAnalytics,
} from "../apis/campaign";
import { toast } from "react-toastify";
import TemplatePreview from "../components/templateCreator/TemplatePreview";
import { transformDuplicateTemplateToCreatorState } from "../utils/templateDataTransformers";
import { applyCampaignVarsToPreviewState } from "../utils/campaignTemplatePreview";

// Theme-token colors (ping.css :root) so icons + chart match the app palette.
const STATUS_CONFIG = {
  overview: { label: "Overview", subLabel: "All buckets", color: "#050505", icon: BarChart3 },
  sent: { label: "Sent", subLabel: "in flight", color: "#0064E0", icon: Send },
  delivered: { label: "Delivered", subLabel: "delivered, not read", color: "#16A34A", icon: CheckCircle },
  read: { label: "Read", subLabel: "read, no reply", color: "#7E57C2", icon: Eye },
  replied: { label: "Replied", subLabel: "any inbound", color: "#B77400", icon: MessageSquare },
  failed: { label: "Failed", subLabel: "failed (terminal)", color: "#E41E3F", icon: XCircle },
  pending: { label: "Pending", subLabel: "not sent yet", color: "#65676B", icon: Clock },
};

// Each outbound message lives in exactly one of these mutually-exclusive
// "farthest state" buckets, so the equation always holds:
//   pending + sent + delivered + read + replied + failed = total_contacts
// That's what the reconciliation strip below visualises.

function syncChartWithSnapshot(rows, analytics) {
  if (!analytics || !rows?.length) return rows || [];
  const point = {
    sent: Math.max(0, analytics.status_counts?.sent ?? 0),
    delivered: analytics.status_counts?.delivered || 0,
    read: analytics.status_counts?.read || 0,
    failed: analytics.status_counts?.failed || 0,
    replied: analytics.reply_count || 0,
  };
  const updated = [...rows];
  updated[updated.length - 1] = { ...updated[updated.length - 1], ...point };
  return updated;
}

const chipForCampaignStatus = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "g";
  if (s === "active" || s === "running") return "b";
  if (s === "failed" || s === "aborted") return "r";
  if (s === "scheduled") return "p";
  if (s === "paused") return "a";
  return "n";
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  // MySQL datetimes are UTC with no offset. Convert to the account timezone
  // (Settings → Timezone, typically Asia/Kolkata) instead of treating the
  // naive string as local/server time.
  return formatTimestampDetailed(dateString, "MMM DD, YYYY hh:mm A") || "—";
};

const CampaignAnalytics = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [summaryData, setSummaryData] = useState(null);
  const [dateWiseData, setDateWiseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [activeTab, setActiveTab] = useState("overview");
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [replyKind, setReplyKind] = useState("all");

  // Template preview carousel
  const [activeTemplateIndex, setActiveTemplateIndex] = useState(0);

  useEffect(() => {
    loadSummary();
  }, [campaignId]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const res = await getCampaignAnalyticsSummary(campaignId);
      if (res.data.success) {
        setSummaryData(res.data.data);
        await loadGraphData(res.data.data?.analytics);
      }
    } catch (error) {
      console.error("Error loading summary:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  // Force-recompute: drops the 30s snapshot cache server-side and re-reads
  // straight from whatsapp_messages. Used when the user wants up-to-the-second
  // numbers without waiting for the TTL.
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await recomputeCampaignAnalytics(campaignId);
      setDateWiseData([]);
      await loadSummary();
      // Reload the active tab list so its total agrees with the new card count
      if (activeTab && activeTab !== "overview") {
        await loadContacts(activeTab, pagination.page || 1, replyKind);
      }
      toast.success("Analytics refreshed");
    } catch (error) {
      console.error("Error refreshing:", error);
      // Fall back to a plain summary reload (no recompute) so users still get a refresh
      await loadSummary();
    } finally {
      setRefreshing(false);
    }
  };

  const loadGraphData = async (analyticsOverride) => {
    try {
      setGraphLoading(true);
      const res = await getCampaignDateWiseAnalytics(campaignId, 30);
      if (res.data.success) {
        const raw = (res.data.data || []).map((d) => ({
          ...d,
          sent: Math.max(0, d.sent ?? 0),
        }));
        const analytics = analyticsOverride || summaryData?.analytics;
        setDateWiseData(syncChartWithSnapshot(raw, analytics));
      }
    } catch (error) {
      console.error("Error loading graph:", error);
    } finally {
      setGraphLoading(false);
    }
  };

  const loadContacts = useCallback(async (status, page = 1, kind = replyKind) => {
    if (!status || status === "overview") return;

    try {
      setContactsLoading(true);
      const extra = status === "replied" ? { reply_kind: kind } : {};
      const res = await getCampaignContactsByStatus(campaignId, status, page, 50, extra);
      if (res.data.success) {
        setContacts(res.data.data || []);
        setPagination({
          page: res.data.pagination.page,
          limit: res.data.pagination.limit,
          total: res.data.pagination.total,
          totalPages: res.data.pagination.totalPages,
        });
      }
    } catch (error) {
      console.error("Error loading contacts:", error);
      toast.error("Failed to load contacts");
    } finally {
      setContactsLoading(false);
    }
  }, [campaignId, replyKind]);

  const handleTabClick = (status) => {
    setActiveTab(status);
    if (status !== "overview") {
      const kind = status === "replied" ? "all" : replyKind;
      if (status === "replied") setReplyKind("all");
      setContacts([]);
      setPagination({ page: 1, limit: 50, total: 0, totalPages: 0 });
      loadContacts(status, 1, kind);
    }
  };

  const handleReplyKindChange = (kind) => {
    setReplyKind(kind);
    setContacts([]);
    setPagination({ page: 1, limit: 50, total: 0, totalPages: 0 });
    loadContacts("replied", 1, kind);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadContacts(activeTab, newPage, replyKind);
    }
  };

  const handleExport = async () => {
    if (!activeTab || activeTab === "overview") return;
    try {
      setExportLoading(true);
      const extra = activeTab === "replied" ? { reply_kind: replyKind } : {};
      const res = await exportCampaignContactsByStatus(campaignId, activeTab, extra);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `campaign_${campaignId}_${activeTab}${activeTab === "replied" && replyKind === "button" ? "_button" : ""}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Exported successfully");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  const handleRetarget = () => {
    navigate(`/campaigns/${campaignId}/retarget?filter=${activeTab}`);
  };

  const getCount = (status) => {
    if (!summaryData?.analytics) return 0;
    if (status === "overview") return summaryData.analytics.total_contacts || 0;
    if (status === "pending") return summaryData.analytics.pending_contacts || 0;
    if (status === "replied") return summaryData.analytics.reply_count || 0;
    const v = summaryData.analytics.status_counts?.[status] ?? 0;
    if (status === "sent" && typeof v === "number" && v < 0) return 0;
    return v;
  };

  const getPercentage = (status) => {
    const total = summaryData?.analytics?.total_contacts || 0;
    if (total === 0) return 0;
    return Math.round((getCount(status) / total) * 100);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload?.length) {
      const fmt = (n) => Number(n || 0).toLocaleString();
      return (
        <div className="canx-tip">
          <p className="canx-tip-date">{label}</p>
          {payload.map((entry, i) => (
            <div key={i} className="canx-tip-row">
              <span className="canx-tip-dot" style={{ background: entry.color }} />
              <span className="canx-tip-name">{entry.name}</span>
              <span className="canx-tip-val" style={{ color: entry.color }}>
                {fmt(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const metricCards = ["sent", "delivered", "read", "replied", "failed"];

  if (loading) {
    return (
      <div className="canx-loading">
        <span className="canx-spin" />
      </div>
    );
  }

  const { campaign, templates, phone, analytics } = summaryData || {};
  const currentTemplate = templates?.[activeTemplateIndex];
  const previewState = currentTemplate
    ? applyCampaignVarsToPreviewState(
        transformDuplicateTemplateToCreatorState(currentTemplate),
        campaign?.variables
      )
    : null;

  return (
    <div className="canx">
      {/* ---------- header ---------- */}
      <button type="button" className="canx-back" onClick={() => navigate("/campaigns")}>
        <ArrowLeft size={15} />
        Back to Campaigns
      </button>

      <div className="page-head">
        <div className="canx-title">
          <h2>{campaign?.campaign_name || "Campaign"}</h2>
          <span className={`ping-chip ${chipForCampaignStatus(campaign?.status)}`}>
            {(campaign?.status || "N/A").toUpperCase()}
          </span>
        </div>
        <div className="grow" />
        <button
          type="button"
          className="ping-btn ping-btn-ghost ping-btn-sm"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw size={15} className={refreshing ? "canx-rotating" : ""} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="canx-meta">
        <span className="canx-meta-item">
          <Users size={13} />
          {(analytics?.total_contacts || 0).toLocaleString()} audience
        </span>
        <span className="canx-meta-item">
          <Calendar size={13} />
          {campaign?.created_at ? new Date(campaign.created_at).toLocaleDateString() : "—"}
        </span>
        {phone && (
          <span className="canx-meta-item">
            <Smartphone size={13} />
            {phone.display_name ? `${phone.display_name} · ` : ""}
            {phone.phone_number}
          </span>
        )}
      </div>

      {/* ---------- status tabs ---------- */}
      <div className="ping-pill-tabs canx-tabs">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const isActive = activeTab === status;
          const Icon = config.icon;
          return (
            <button
              key={status}
              type="button"
              onClick={() => handleTabClick(status)}
              className={`ping-ptab ${isActive ? "on" : ""}`}
            >
              <Icon size={14} style={isActive ? undefined : { color: config.color }} />
              {config.label}
              <span className="canx-ptab-count">{getCount(status).toLocaleString()}</span>
              {status !== "overview" && (
                <span className="canx-ptab-pct">{getPercentage(status)}%</span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === "overview" ? (
        <div className="canx-grid">
          {/* ---------- sidebar: sender + template preview ---------- */}
          <aside className="canx-side">
            {phone && (
              <div className="ping-card canx-phone">
                <span className="canx-phone-ico">
                  <Smartphone size={16} />
                </span>
                <div className="canx-phone-txt">
                  <b>{phone.display_name || "WhatsApp Business"}</b>
                  <span>{phone.phone_number}</span>
                </div>
              </div>
            )}

            {templates && templates.length > 0 ? (
              <div className="ping-card canx-tpl">
                <div className="canx-tpl-head">
                  <span className="canx-tpl-label">
                    Template {activeTemplateIndex + 1}/{templates.length}
                  </span>
                  {templates.length > 1 && (
                    <span className="canx-tpl-nav">
                      <button
                        type="button"
                        onClick={() => setActiveTemplateIndex((i) => Math.max(0, i - 1))}
                        disabled={activeTemplateIndex === 0}
                      >
                        <ChevronLeft size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setActiveTemplateIndex((i) => Math.min(templates.length - 1, i + 1))
                        }
                        disabled={activeTemplateIndex === templates.length - 1}
                      >
                        <ChevronRight size={15} />
                      </button>
                    </span>
                  )}
                </div>
                <div className="canx-tpl-name">
                  <b>{currentTemplate?.name}</b>
                  <span>
                    {currentTemplate?.language} · {currentTemplate?.category}
                  </span>
                </div>
                {previewState ? (
                  <TemplatePreview state={previewState} />
                ) : (
                  <div className="canx-empty small">Preview not available</div>
                )}
              </div>
            ) : (
              <div className="ping-card canx-empty small">No templates found</div>
            )}
          </aside>

          {/* ---------- main analytics ---------- */}
          <div className="canx-main">
            <div className="canx-stats">
              {metricCards.map((status) => {
                const config = STATUS_CONFIG[status];
                const Icon = config.icon;
                return (
                  <button
                    key={status}
                    type="button"
                    onClick={() => handleTabClick(status)}
                    className="ping-card ping-stat canx-stat"
                  >
                    <span className="canx-stat-top">
                      <Icon size={15} style={{ color: config.color }} />
                      <span className="canx-stat-pct">{getPercentage(status)}%</span>
                    </span>
                    <span className="v">{getCount(status).toLocaleString()}</span>
                    <span className="k">{config.label}</span>
                    <span className="canx-stat-sub">{config.subLabel}</span>
                  </button>
                );
              })}
            </div>

            <div className="ping-banner ok canx-strip">
              <span>
                <b>{(analytics?.pending_contacts || 0).toLocaleString()}</b> pending
              </span>
              <span className="canx-strip-sep" />
              <span>
                <b>{(analytics?.total_contacts || 0).toLocaleString()}</b> total contacts
              </span>
              {(analytics?.unaccounted ?? 0) !== 0 && (
                <>
                  <span className="canx-strip-sep" />
                  <span className="canx-strip-warn">
                    Δ {(analytics?.unaccounted || 0).toLocaleString()} unaccounted
                  </span>
                </>
              )}
            </div>

            <div className="ping-card canx-chart">
              <h3>Status over time</h3>
              <p className="sub">End-of-day bucket counts — same definitions as the cards above</p>
              <div className="canx-chart-box">
                {graphLoading ? (
                  <div className="canx-loading tall">
                    <span className="canx-spin" />
                  </div>
                ) : dateWiseData.length === 0 ? (
                  <div className="canx-empty tall">
                    <BarChart3 size={36} />
                    <p>No timeline data yet</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dateWiseData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        {metricCards.map((s) => (
                          <linearGradient key={s} id={`canx-${s}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={STATUS_CONFIG[s].color} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={STATUS_CONFIG[s].color} stopOpacity={0.04} />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E4E6EB" vertical={false} />
                      <XAxis
                        dataKey="date"
                        stroke="#8A8D91"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => {
                          const d = new Date(v);
                          return `${d.getDate()}/${d.getMonth() + 1}`;
                        }}
                      />
                      <YAxis
                        stroke="#8A8D91"
                        fontSize={11}
                        tickLine={false}
                        axisLine={false}
                        width={48}
                        tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "12px" }} />
                      {["failed", "replied", "read", "delivered", "sent"].map((s) => (
                        <Area
                          key={s}
                          type="monotone"
                          dataKey={s}
                          stackId="status"
                          stroke={STATUS_CONFIG[s].color}
                          strokeWidth={1.5}
                          fill={`url(#canx-${s})`}
                          name={STATUS_CONFIG[s].label}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ---------- contact list for the selected bucket ---------- */
        <div className="ping-card canx-list">
          <div className="canx-list-head">
            <span
              className="canx-list-ico"
              style={{ color: STATUS_CONFIG[activeTab].color }}
            >
              {React.createElement(STATUS_CONFIG[activeTab].icon, { size: 18 })}
            </span>
            <div className="canx-list-title">
              <h3>
                {activeTab === "replied" && replyKind === "button"
                  ? "Button replies"
                  : `${STATUS_CONFIG[activeTab].label} contacts`}
              </h3>
              <p className="sub">{pagination.total.toLocaleString()} contacts</p>
            </div>
            {activeTab === "replied" && (
              <div className="canx-reply-split" role="tablist" aria-label="Reply type">
                <button
                  type="button"
                  role="tab"
                  aria-selected={replyKind === "all"}
                  className={replyKind === "all" ? "on" : ""}
                  onClick={() => handleReplyKindChange("all")}
                >
                  Overall
                  <b>{(summaryData?.analytics?.reply_count || 0).toLocaleString()}</b>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={replyKind === "button"}
                  className={replyKind === "button" ? "on" : ""}
                  onClick={() => handleReplyKindChange("button")}
                >
                  <MousePointerClick size={13} />
                  Button
                  <b>{(summaryData?.analytics?.button_reply_count || 0).toLocaleString()}</b>
                </button>
              </div>
            )}
            <div className="grow" />
            <button
              type="button"
              className="ping-btn ping-btn-ghost ping-btn-sm"
              onClick={handleExport}
              disabled={exportLoading || pagination.total === 0}
            >
              {exportLoading ? <span className="canx-spin sm" /> : <Download size={15} />}
              Export CSV
            </button>
            <button
              type="button"
              className="ping-btn ping-btn-green ping-btn-sm"
              onClick={handleRetarget}
            >
              <Target size={15} />
              Retarget
            </button>
          </div>

          <div className="canx-table-wrap">
            {contactsLoading ? (
              <div className="canx-loading tall">
                <span className="canx-spin" />
              </div>
            ) : contacts.length === 0 ? (
              <div className="canx-empty tall">
                <Users size={36} />
                <p>No contacts found</p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Phone</th>
                    {activeTab === "sent" && <th>Sent At</th>}
                    {activeTab === "delivered" && <th>Delivered At</th>}
                    {activeTab === "read" && <th>Read At</th>}
                    {activeTab === "replied" && (
                      <>
                        <th>Customer Name</th>
                        <th>Type</th>
                        <th>Reply</th>
                        <th>Reply At</th>
                      </>
                    )}
                    {activeTab === "failed" && (
                      <>
                        <th>Failed Reason</th>
                        <th>Failed At</th>
                      </>
                    )}
                    {activeTab === "pending" && <th>Variables</th>}
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, i) => (
                    <tr key={c.contact_id || i}>
                      <td>
                        <span className="canx-cell-phone">
                          <Phone size={13} />
                          {c.phone || "—"}
                        </span>
                      </td>
                      {activeTab === "sent" && <td>{formatDate(c.sent_at)}</td>}
                      {activeTab === "delivered" && <td>{formatDate(c.delivered_at)}</td>}
                      {activeTab === "read" && <td>{formatDate(c.read_at)}</td>}
                      {activeTab === "replied" && (
                        <>
                          <td className="canx-cell-strong">{c.customer_name || "—"}</td>
                          <td>
                            <span className={`ping-chip ${c.reply_kind === "button" ? "a" : "n"}`}>
                              {c.reply_kind === "button" ? "Button" : "Message"}
                            </span>
                          </td>
                          <td className="canx-cell-clip" title={c.reply || ""}>
                            {c.reply || "—"}
                          </td>
                          <td>{formatDate(c.reply_at)}</td>
                        </>
                      )}
                      {activeTab === "failed" && (
                        <>
                          <td className="canx-cell-fail" title={c.failed_reason || ""}>
                            {c.failed_reason || "—"}
                          </td>
                          <td>{formatDate(c.failed_at)}</td>
                        </>
                      )}
                      {activeTab === "pending" && (
                        <td>
                          {c.variables && Object.keys(c.variables).length > 0 ? (
                            <span className="canx-vars">
                              {Object.entries(c.variables).map(([key, value]) => (
                                <span key={key} className="canx-var" title={`${key}: ${value}`}>
                                  <b>{key}:</b> {String(value)}
                                </span>
                              ))}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {pagination.totalPages > 1 && (
            <div className="canx-pager">
              <span className="canx-pager-info">
                {(pagination.page - 1) * pagination.limit + 1} –{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
                {pagination.total.toLocaleString()}
              </span>
              <div className="canx-pager-btns">
                <button
                  type="button"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft size={15} />
                </button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let p;
                  if (pagination.totalPages <= 5) p = i + 1;
                  else if (pagination.page <= 3) p = i + 1;
                  else if (pagination.page >= pagination.totalPages - 2)
                    p = pagination.totalPages - 4 + i;
                  else p = pagination.page - 2 + i;
                  return (
                    <button
                      type="button"
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={pagination.page === p ? "on" : ""}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CampaignAnalytics;
